<script setup lang="ts">
import type { Music,GenMusic } from '~/services/musicService';
import { musicService } from '~/services/musicService';
import { getSectionByPath, generateTitleFromPath, getSectionById } from '~/services/musicSectionMapConfig';
import { useI18n, useRoute, useRouter, ref, onMounted, onUnmounted, watch, useHead, useSeoMeta } from '#imports';
import { usePagination } from '~/composables/usePagination';
import MusicList from '~/components/music/MusicList.vue';
import { useAuthState } from '~/composables/useAuthState';
import { useToast } from '~/composables/useToast';
import { useMusicCreate } from '~/composables/useMusicCreate';

const { user, userAccount,handleSignIn } = useAuthState();
const { saveMusicData } = useMusicCreate();
const toast = useToast();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// 状态
const chartTitle = ref('');
const sectionParam = route.params.section as string;

// 判断URL类型：新的/music/[section]格式，或者旧的查询参数格式
const isLegacyUrl = !sectionParam && (route.query.chart || route.query.category);
const sectionInfo = sectionParam ? getSectionByPath(sectionParam) : null;

// 设置标题
if (sectionParam && !isLegacyUrl) {
  // 新路由格式：从sectionInfo或URL路径生成标题
  chartTitle.value = sectionInfo?.title || generateTitleFromPath(sectionParam);
} else {
  // 旧格式：直接使用查询参数中的title
  chartTitle.value = route.query.title as string;
}

// 分页相关状态
const currentPage = ref(parseInt(route.query.page as string) || 1);
const totalPages = ref(1);

// 动态生成获取数据的函数
const fetchMusicData = async (pageNum: number) => {
  const pageSize = 20;
  
  // 处理不同路由格式
  if (sectionParam && !isLegacyUrl && sectionInfo) {
    // 新路由格式
    if (sectionInfo.type === 'rank') {
      
      const response = await musicService.getMoreChartMusic(
        sectionInfo.id, 
        sectionInfo.selected_option, 
        { page: pageNum, size: pageSize }
      );
      
      if (response && response.total) {
        totalPages.value = Math.ceil(response.total / pageSize);
      }
      return response;
    } else {
      // 分类数据 - 直接使用section作为search_tag
      const response = await musicService.getMoreCategoryMusic(sectionParam, { page: pageNum, size: pageSize });
      if (response && response.total) {
        totalPages.value = Math.ceil(response.total / pageSize);
      }
      return response;
    }
  } else {
    // 兼容旧格式
    const chartId = route.query.chart as string;
    const category = route.query.category as string;
    
    if (chartId) {
      // 尝试获取该榜单的section_option配置
      const response = await musicService.getMoreChartMusic(chartId, undefined, { page: pageNum, size: pageSize });
      if (response && response.total) {
        totalPages.value = Math.ceil(response.total / pageSize);
      }
      return response;
    } else if (category) {
      const response = await musicService.getMoreCategoryMusic(category, { page: pageNum, size: pageSize });
      if (response && response.total) {
        totalPages.value = Math.ceil(response.total / pageSize);
      }
      return response;
    } else {
      throw new Error(t('music.invalid_params'));
    }
  }
};

// 使用分页hooks
const {
  list: musicList,
  isLoading,
  isLoadingMore,
  error,
  loadMoreTrigger,
  loadData,
  hasMore,
  loadMore
} = usePagination<Music>(
  fetchMusicData,
  {
    pageSize: 20,
    initialPage: currentPage.value,
    errorHandler: (err) => {
      console.error(`加载音乐数据失败:`, err);
      error.value = t('common.load_failed');
    }
  }
);

let obj:any = {
  title: chartTitle.value || 'Music Collection',
  description: `${chartTitle.value} - Discover premium music collection, offering rich music resources and playback experience`,
  keywords: `${chartTitle.value},music,online music,music playback,music resources`,
  ogTitle: `${chartTitle.value}`,
  ogDescription: `Browse selected ${chartTitle.value} music, quality track recommendations`,
}

// 根据数据类型设置不同的描述
if (sectionParam && sectionInfo) {
  if (sectionInfo.type === 'category') {
    // 分类页面描述
    obj.description = t('meta.musicSection.description_category', { section: chartTitle.value });
    obj.keywords = t('meta.musicSection.keywords_category', { section: chartTitle.value });
    obj.ogTitle = t('meta.musicSection.ogTitle_category', { section: chartTitle.value });
    obj.ogDescription = t('meta.musicSection.ogDescription_category', { section: chartTitle.value });
  } else {
    // 榜单页面描述
    obj.description = t('meta.musicSection.description_rank', { section: chartTitle.value });
    obj.keywords = t('meta.musicSection.keywords_rank', { section: chartTitle.value });
    obj.ogTitle = t('meta.musicSection.ogTitle_rank', { section: chartTitle.value });
    obj.ogDescription = t('meta.musicSection.ogDescription_rank', { section: chartTitle.value });
  }
} else if (route.query.category) {
  // 兼容旧分类URL
  obj.description = t('meta.musicSection.description_category', { section: chartTitle.value });
  obj.keywords = t('meta.musicSection.keywords_category', { section: chartTitle.value });
  obj.ogTitle = t('meta.musicSection.ogTitle_category', { section: chartTitle.value });
  obj.ogDescription = t('meta.musicSection.ogDescription_category', { section: chartTitle.value });
} else if (route.query.chart) {
  // 兼容旧榜单URL
  obj.description = t('meta.musicSection.description_rank', { section: chartTitle.value });
  obj.keywords = t('meta.musicSection.keywords_rank', { section: chartTitle.value });
  obj.ogTitle = t('meta.musicSection.ogTitle_rank', { section: chartTitle.value });
  obj.ogDescription = t('meta.musicSection.ogDescription_rank', { section: chartTitle.value });
}

// SEO配置
useSeoMeta({
  title: 'MusicMint - ' + obj.title + ' Music Collection',
  description: obj.description,
  keywords: obj.keywords,
  ogTitle: obj.ogTitle,
  ogDescription: obj.ogDescription,
  ogImage: '/images/ogimage.png',
  twitterCard: 'summary_large_image',
})

useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": obj.title,
        "description": obj.description,
        "url": `${process.env.BASE_URL || 'https://musicmint.ai'}${route.fullPath}`,
        "mainEntity": {
          "@type": "MusicPlaylist",
          "name": obj.title,
          "numTracks": musicList.value?.length || 0,
          "track": musicList.value?.map((music, index) => ({
            "@type": "MusicRecording",
            "position": index + 1,
            "name": music.title || music.metadata?.prompt || '',
            "duration": music.metadata?.duration ? `PT${Math.floor(music.metadata.duration / 60)}M${Math.floor(music.metadata.duration % 60)}S` : undefined,
            "genre": music.metadata?.tags?.split(',').map(tag => tag.trim()) || [],
            "description": music.metadata?.prompt || '',
            "url": `${process.env.BASE_URL || 'https://musicmint.ai'}/music/song/${music.id}`
          }))
        }
      })
    }
  ]
})

// 监听路由变化，当页码参数变化时重新加载数据
watch(() => route.query.page, (newPage) => {
  const pageNum = parseInt(newPage as string) || 1;
  if (pageNum !== currentPage.value) {
    currentPage.value = pageNum;
    loadData(pageNum); // 强制重新加载指定页码的数据
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}, { immediate: true });

// 修改导航函数，使其保持当前路由格式
const navigateToPage = (page: number) => {
  if (page < 1 || page > totalPages.value) return;
  
  if (sectionParam) {
    // 新路由格式：/music/[section]?page=x
    router.push({ path: route.path, query: { page: page.toString() } });
  } else {
    // 旧路由格式：保留所有查询参数，更新page
    const query = { ...route.query, page: page.toString() };
    router.push({ path: route.path, query });
  }
};
</script>

<template>
  <div class="music-list-page">
    <div class="lg:px-4">
      <!-- 页面标题 -->
      <div class="mb-4 lg:mb-8">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
          {{ chartTitle || t('music.library') }}
        </h1>
      </div>
      
      <MusicList
        :music-list="musicList"
        :is-loading="isLoading"
        :is-loading-more="isLoadingMore"
        :has-more="hasMore"
        :error="error"
        :show-create-similar="true"
        @reload="() => loadData(1)"
        @load-more="loadMore"
      />
      
      <!-- 对搜索引擎可见但对用户隐藏的分页导航 -->
      <div aria-hidden="true" class="sr-only">
        <div class="pagination-index">
          <span>{{ t('common.page') }}: {{ currentPage }} / {{ totalPages }}</span>
          <ul>
            <!-- 首页 -->
            <li v-if="currentPage > 1">
              <a :href="`${route.path}?${route.query.chart ? 'chart=' + route.query.chart + '&' : ''}${route.query.category ? 'category=' + route.query.category + '&' : ''}${route.query.title ? 'title=' + route.query.title + '&' : ''}page=1`"
                 @click.prevent="navigateToPage(1)">
                {{ t('common.firstPage') }}
              </a>
            </li>
            
            <!-- 上一页 -->
            <li v-if="currentPage > 1">
              <a :href="`${route.path}?${route.query.chart ? 'chart=' + route.query.chart + '&' : ''}${route.query.category ? 'category=' + route.query.category + '&' : ''}${route.query.title ? 'title=' + route.query.title + '&' : ''}page=${currentPage - 1}`"
                 @click.prevent="navigateToPage(currentPage - 1)">
                {{ t('common.prevPage') }}
              </a>
            </li>
            
            <!-- 页码列表 -->
            <li v-for="page in Math.min(10, totalPages)" :key="page">
              <a :href="`${route.path}?${route.query.chart ? 'chart=' + route.query.chart + '&' : ''}${route.query.category ? 'category=' + route.query.category + '&' : ''}${route.query.title ? 'title=' + route.query.title + '&' : ''}page=${page}`"
                 @click.prevent="navigateToPage(page)">
                {{ page }}
              </a>
            </li>
            
            <!-- 下一页 -->
            <li v-if="currentPage < totalPages">
              <a :href="`${route.path}?${route.query.chart ? 'chart=' + route.query.chart + '&' : ''}${route.query.category ? 'category=' + route.query.category + '&' : ''}${route.query.title ? 'title=' + route.query.title + '&' : ''}page=${currentPage + 1}`"
                 @click.prevent="navigateToPage(currentPage + 1)">
                {{ t('common.nextPage') }}
              </a>
            </li>
            
            <!-- 末页 -->
            <li v-if="currentPage < totalPages">
              <a :href="`${route.path}?${route.query.chart ? 'chart=' + route.query.chart + '&' : ''}${route.query.category ? 'category=' + route.query.category + '&' : ''}${route.query.title ? 'title=' + route.query.title + '&' : ''}page=${totalPages}`"
                 @click.prevent="navigateToPage(totalPages)">
                {{ t('common.lastPage') }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- 添加一个可见的分页控件用于测试 -->
      <div v-if="totalPages > 1" class="flex justify-center mt-8 mb-4 space-x-2">
        <button 
          v-if="currentPage > 1"
          @click="navigateToPage(currentPage - 1)" 
          class="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white"
        >
          {{ t('common.prevPage') }}
        </button>
        
        <span class="px-4 py-2 rounded bg-gray-800 text-white">
          {{ currentPage }} / {{ totalPages }}
        </span>
        
        <button 
          v-if="currentPage < totalPages"
          @click="navigateToPage(currentPage + 1)" 
          class="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white"
        >
          {{ t('common.nextPage') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.genre-filter {
  scrollbar-width: none; /* Firefox */
}

.genre-filter::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Edge */
}

/* 对SEO友好的分页，在视觉上隐藏 */
.pagination-index {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style> 